import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { toast } from "react-hot-toast";
import { SavedPreference } from "../types/user";
import { UserRole, UserProfile } from "../types";


// --- 1. GET PROFILE ---
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return { uid, ...userSnap.data() } as UserProfile;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

// --- helper: normalize phone ---
const normalizePhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  const last10 = digits.slice(-10);
  return `+91${last10}`;
};

// --- 2. UPDATE PROFILE ---
export const updateUserProfile = async (
  uid: string,
  updates: {
    name?: string;
    address?: string;
    homeAddress?: string;
    phone?: string;
homeAddressMeta?: {
  mode: "community" | "other";
  communityId?: string;
  communityName?: string;
  block?: string;
  floor?: string;
  flat?: string;
  houseNumber?: string;
  location?: { lat: number; lng: number };
  rawAddressFromMap?: string;
};
officeAddress?: string;
officeLocation?: { lat: number; lng: number };

  }
): Promise<void> => {
  try {
    const userRef = doc(db, "users", uid);
    console.log("Updating user profile:", uid, updates);

    const payload: any = {
      ...updates,
      updatedAt: new Date(),
    };

    if (updates.phone) {
      payload.phone = normalizePhone(updates.phone);
    }

    // Single write, merge into existing doc (no new doc, no overwrite)
    await setDoc(userRef, payload, { merge: true });

    console.log("Profile updated successfully in Firestore");
  } catch (error: any) {
    console.error("Error updating profile:", error);
    throw new Error(error.message || "Failed to update profile");
  }
};

// --- 3. TOGGLE FAVORITE (FIXED SIGNATURE) ---
export const toggleUserFavorite = async (uid: string, favoriteId: string, currentState?: boolean) => {
  try {
    const userRef = doc(db, "users", uid);

    if (typeof currentState === "boolean") {
      if (currentState) {
        await updateDoc(userRef, { favorites: arrayRemove(favoriteId) });
        toast.success("Removed from favorites");
      } else {
        await updateDoc(userRef, { favorites: arrayUnion(favoriteId) });
        toast.success("Added to favorites");
      }
      return;
    }

    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const favorites = userData.favorites || [];

      if (favorites.includes(favoriteId)) {
        await updateDoc(userRef, { favorites: arrayRemove(favoriteId) });
        toast.success("Removed from favorites");
      } else {
        await updateDoc(userRef, { favorites: arrayUnion(favoriteId) });
        toast.success("Added to favorites");
      }
    }
  } catch (error) {
    console.error("Error toggling favorite:", error);
    toast.error("Failed to update favorites");
  }
};

// --- 4. SAVE PREFERENCE ---
export const saveUserPreference = async (uid: string, item: SavedPreference) => {
  try {
    const userRef = doc(db, "users", uid);

    await updateDoc(userRef, {
      savedItems: arrayUnion(item),
    });

    toast.success(`Saved to ${item.type} list!`);
  } catch (error: any) {
    console.error("Error saving preference:", error);
    toast.error("Failed to save preference.");
  }
};

// --- 5. REMOVE PREFERENCE ---
export const removeUserPreference = async (uid: string, item: SavedPreference) => {
  try {
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, {
      savedItems: arrayRemove(item),
    });
    toast.success("Item removed from preferences.");
  } catch (error: any) {
    console.error("Error removing preference:", error);
    toast.error("Failed to remove item.");
  }
};
/**
 * Add a role to a user (admin action)
 */
export const addRoleToUser = async (uid: string, role: UserRole): Promise<void> => {
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error("User not found");
    }
    
    const currentRoles = userSnap.data().roles || ["customer"];
    
    if (!currentRoles.includes(role)) {
      await updateDoc(userRef, {
        roles: [...currentRoles, role],
      });
      console.log(`Role ${role} added to user ${uid}`);
    }
  } catch (error) {
    console.error("Error adding role to user:", error);
    throw error;
  }
};

/**
 * Remove a role from a user (admin action)
 */
export const removeRoleFromUser = async (uid: string, role: UserRole): Promise<void> => {
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error("User not found");
    }
    
    let currentRoles = userSnap.data().roles || ["customer"];
    
    if (currentRoles.length === 1 && currentRoles[0] === role) {
      throw new Error("Cannot remove the only role. User must have at least one role.");
    }
    
    const updatedRoles = currentRoles.filter((r: UserRole) => r !== role);
    const currentRole = userSnap.data().currentRole;
    const newCurrentRole = currentRole === role ? updatedRoles[0] : currentRole;
    
    await updateDoc(userRef, {
      roles: updatedRoles,
      currentRole: newCurrentRole,
    });
    
    console.log(`Role ${role} removed from user ${uid}`);
  } catch (error) {
    console.error("Error removing role from user:", error);
    throw error;
  }
};

/**
 * Switch user's currently active role
 */
export const switchUserRole = async (uid: string, newRole: UserRole): Promise<void> => {
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error("User not found");
    }
    
    const roles = userSnap.data().roles || ["customer"];
    
    if (!roles.includes(newRole)) {
      throw new Error(`User does not have role: ${newRole}`);
    }
    
    await updateDoc(userRef, {
      currentRole: newRole,
    });
    
    console.log(`User ${uid} switched to role: ${newRole}`);
  } catch (error) {
    console.error("Error switching user role:", error);
    throw error;
  }
};

/**
 * Helper: Check if user has a specific role
 */
export const userHasRole = (user: UserProfile, role: UserRole): boolean => {
  return user.roles?.includes(role) || false;
};

/**
 * Helper: Check if user is admin
 */
export const isUserAdmin = (user: UserProfile): boolean => {
  return userHasRole(user, "admin");
};

